import React, { useContext, useMemo } from "react";
import MultiSelectDropdown from "../MultiSelectDropdown";
import { UserContext } from "shared/context/UserContext";
import {
  MergeRememberedMmids,
  ReconcileSelectedMmids,
} from "shared/helpers/SelectedLocationMemory";

export default function MemberLocationDropdown(props) {
  const {
    onChange,
    programSubscription,
    customMemberLocationList = null,
  } = props;
  const userContext = useContext(UserContext);

  //NOTE: Depending on the programSubscription being passed, determine which set of locations to display data for
  let memberLocations;
  if (programSubscription === "Rec2.0") {
    memberLocations = userContext.recLocations;
  } else if (programSubscription === "Metric2.0") {
    memberLocations = userContext.metricLocations;
  } else {
    memberLocations = userContext.locations;
  }

  const options = customMemberLocationList
    ? customMemberLocationList
    : memberLocations;

  // A caller supplying its own list is showing member pharmacies the user did not choose from the
  // header, so it neither restores nor contributes to the remembered selection.
  const isRemembered = !customMemberLocationList;

  const rememberedMmids = userContext.selectedLocationMmids;

  // The selection is remembered as MMID values, but MultiSelectDropdown wants option objects and
  // anything no longer on offer has to be dropped. Memoized because it feeds an effect that
  // compares defaultSelected by value - a new array every render would re-run it endlessly.
  const restoredSelection = useMemo(() => {
    if (!isRemembered) return [];

    return ReconcileSelectedMmids(options, rememberedMmids);
  }, [isRemembered, options, rememberedMmids]);

  // No remembered selection - including the case where nothing remembered is still on offer -
  // falls back to selecting everything, which is what this dropdown has always done. Restoring a
  // subset needs onLoadAllSelected off: MultiSelectDropdownBody bases every option to that flag
  // whenever the options change, so leaving it on would re-select the whole list.
  const hasRestoredSelection = restoredSelection.length > 0;

  function handleChange(selectedValues) {
    onChange(selectedValues);

    if (!isRemembered || !Array.isArray(options)) return;

    // The dropdown reports its selection on mount as well as on a click. Recording "everything"
    // when there was nothing to remember in the first place would turn that opening report into a
    // selection, so it is left alone until the user actually narrows the list.
    const isUntouchedSelectAll =
      rememberedMmids.length === 0 && selectedValues.length === options.length;

    if (isUntouchedSelectAll) return;

    userContext.onSelectedLocationMmidsChange(
      MergeRememberedMmids(rememberedMmids, options, selectedValues)
    );
  }

  return (
    options && (
      <MultiSelectDropdown
        options={options}
        onChange={handleChange}
        defaultSelected={restoredSelection}
        selectAllOption
        filterable
        onLoadAllSelected={!hasRestoredSelection}
      />
    )
  );
}
